import { useEffect } from 'react';
import { useAppDispatch } from '../app/hooks';
import { checkService } from '../services/CheckService';
import { checkIntegrationService } from '../services/CheckIntegrationService';
import { initializeState } from '../app/thunks/init-state';
import { changeStep } from '../app/slices/step.slice';
import { reportsActions } from '../app/slices/report.slice';
import { CheckIntegrationStatus, IdentitySource, IdentitySourceName } from '../types/check/types';

const POLL_INTERVAL_MS = 5000;

const findPosPhotoIdent = (identity?: IdentitySource[]): IdentitySource | undefined => {
	const matches = identity?.filter((entry) => entry.source === IdentitySourceName.POS_PHOTO_IDENT) ?? [];
	return matches.length ? matches[matches.length - 1] : undefined;
};

const TERMINAL_STATUSES: Array<string> = [
	CheckIntegrationStatus.SUCCESS,
	CheckIntegrationStatus.ACCEPTED,
	CheckIntegrationStatus.ERROR,
	CheckIntegrationStatus.CANCELLED
];

export const useObservePosIdentSession = () => {
	const dispatch = useAppDispatch();

	useEffect(() => {
		let cancelled = false;
		let redirecting = false;
		let pendingSeen = false;
		let sessionCreationRequested = false;

		const tick = async () => {
			if (cancelled || redirecting) return;

			const check = await checkService.fetchCheckReport();
			dispatch(reportsActions.setIdentity(check.persona?.identity));
			const identity = findPosPhotoIdent(check.persona?.identity);
			if (!identity || cancelled) return;

			const { status, metadata } = identity;

			if (status === CheckIntegrationStatus.ERROR) {
				dispatch(changeStep('POS_IDENT_ERROR_STEP'));
				return;
			}

			if (TERMINAL_STATUSES.includes(status)) {
				dispatch(initializeState());
				return;
			}

			const redirectUrl = metadata?.redirectUrl ?? metadata?.IdentificationUrl;
			const alreadyRedirected = metadata?.isRedirected === true || metadata?.sessionRedirected === true;

			if (redirectUrl && !alreadyRedirected) {
				redirecting = true;
				await checkIntegrationService.markPosIdentRedirection();
				window.location.href = redirectUrl;
				return;
			}

			if (status === CheckIntegrationStatus.PROCESSING) {
				await checkIntegrationService.checkPosIdentStatus();
				return;
			}

			if (status === CheckIntegrationStatus.PENDING) {
				if (!pendingSeen) {
					pendingSeen = true;
				} else if (!sessionCreationRequested) {
					sessionCreationRequested = true;
					await checkIntegrationService.createPosIdentSession();
				}
			}
		};

		const ref = setInterval(() => {
			tick().catch(() => {});
		}, POLL_INTERVAL_MS);

		const onPageShow = (event: PageTransitionEvent) => {
			if (event.persisted) {
				redirecting = false;
				tick().catch(() => {});
			}
		};
		const onVisibilityChange = () => {
			if (document.visibilityState === 'visible') {
				tick().catch(() => {});
			}
		};
		window.addEventListener('pageshow', onPageShow);
		document.addEventListener('visibilitychange', onVisibilityChange);

		tick().catch(() => {});

		return () => {
			cancelled = true;
			clearInterval(ref);
			window.removeEventListener('pageshow', onPageShow);
			document.removeEventListener('visibilitychange', onVisibilityChange);
		};
	}, [dispatch]);
};
